import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import Loader from '../../components/Loader';
import { toast } from 'react-toastify';
import ApiService from '../../services/ApiService';
import { Dropdown } from 'primereact/dropdown';
import { formatDate, formatCurrency, formatNumberWithCommas, parseNumberFromFormatted } from '../../utils/helper';

const AddUpdate = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [isLoader, setIsLoader] = useState(false);
    const [products, setProducts] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [supplierType, setSupplierType] = useState('existing');
    const [newSupplier, setNewSupplier] = useState({ name: '', phone: '', email: '', address: '' });
    
    const [formData, setFormData] = useState({
        purchase_date: new Date().toISOString().split('T')[0],
        supplier_id: null,
        supplier_name: '',
        status: 'received',
        note: '',
        items: []
    });

    useEffect(() => {
        fetchProducts();
        fetchSuppliers();
        if (id) {
            getSingleData();
        } else {
            addItem();
        }
    }, [id]);

    const fetchSuppliers = async () => {
        try {
            const response = await ApiService.request({
                method: 'GET',
                url: 'admin/suppliers?limit=1000'
            });
            if (response.data.status) {
                setSuppliers(response.data.data.suppliers);
            }
        } catch (error) {
            console.error('Failed to fetch suppliers', error);
        }
    };

    const fetchProducts = async () => {
        try {
            // Fetch all products for dropdown
            const response = await ApiService.request({
                method: 'GET',
                url: 'admin/products?limit=1000' // Try to get many
            });
            if (response.data.status) {
                setProducts(response.data.data.products);
            }
        } catch (error) {
            console.error('Failed to fetch products', error);
        }
    };

    const getSingleData = async () => {
        setIsLoader(true);
        try {
            const response = await ApiService.request({
                method: 'GET',
                url: `admin/purchases/${id}`,
            });
            const data = response.data;
            if (data.status) {
                const purchase = data.data.purchase;
                setFormData({
                    purchase_date: purchase.purchase_date,
                    supplier_id: purchase.supplier_id || null,
                    supplier_name: purchase.supplier_name || '',
                    status: purchase.status,
                    note: purchase.note || '',
                    items: purchase.items.map(item => ({
                        product_id: item.product_id,
                        quantity: item.quantity,
                        unit: item.unit || 'piece',
                        unit_price: item.unit_price,
                        subtotal: item.subtotal || (item.quantity * item.unit_price) // Ensure subtotal
                    }))
                });
                if (purchase.supplier_id) {
                    setSupplierType('existing');
                } else if (purchase.supplier_name) {
                    // Legacy support or ad-hoc
                    setSupplierType('new');
                    setNewSupplier(prev => ({ ...prev, name: purchase.supplier_name }));
                }
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            console.error(error);
            toast.error('Failed to load purchase data');
        } finally {
            setIsLoader(false);
        }
    };

    const addItem = () => {
        setFormData(prev => ({
            ...prev,
            items: [...prev.items, { product_id: '', quantity: 1, unit: 'piece', unit_price: 0, subtotal: 0 }]
        }));
    };

    const removeItem = (index) => {
        const newItems = formData.items.filter((_, i) => i !== index);
        setFormData({ ...formData, items: newItems });
    };

    const isProductLens = (productId) => {
        const product = products.find(p => p.id === productId);
        return product?.category?.name === 'Lens';
    };

    const handleItemChange = (index, field, value) => {
        const newItems = [...formData.items];
        
        // If product changes, check if it's a Lens
        if (field === 'product_id') {
            const isLens = isProductLens(value);
            if (!isLens) {
                newItems[index].unit = 'piece';
            }
        }

        if (field === 'unit_price') {
            newItems[index][field] = parseNumberFromFormatted(value);
        } else {
            newItems[index][field] = value;
        }
        
        // Calculate subtotal
        if (field === 'quantity' || field === 'unit_price') {
            const qty = parseFloat(newItems[index].quantity) || 0;
            const price = parseFloat(newItems[index].unit_price) || 0;
            newItems[index].subtotal = qty * price;
        }

        setFormData({ ...formData, items: newItems });
    };

    const calculateTotal = () => {
        return formData.items.reduce((acc, item) => acc + (parseFloat(item.subtotal) || 0), 0);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validation
        if (formData.items.length === 0) {
            toast.error('Please add at least one item');
            return;
        }
        for (let item of formData.items) {
            if (!item.product_id) {
                toast.error('Please select a product for all items');
                return;
            }
            if (item.quantity <= 0) {
                toast.error('Quantity must be greater than 0');
                return;
            }
        }

        setIsLoader(true);
        try {
            let supplierId = formData.supplier_id;
            let supplierName = formData.supplier_name;

            if (supplierType === 'new') {
                if (!newSupplier.name) {
                    toast.error('Supplier name is required');
                    setIsLoader(false);
                    return;
                }
                
                // Create supplier first
                const supplierResponse = await ApiService.request({
                    method: 'POST',
                    url: 'admin/suppliers',
                    data: newSupplier
                });
                
                if (supplierResponse.data.status) {
                    supplierId = supplierResponse.data.data.supplier.id;
                    supplierName = supplierResponse.data.data.supplier.name;
                } else {
                    toast.error(supplierResponse.data.message || 'Failed to create supplier');
                    setIsLoader(false);
                    return;
                }
            } else if (!supplierId) {
                 toast.error('Please select a supplier');
                 setIsLoader(false);
                 return;
            }

            const payload = {
                ...formData,
                supplier_id: supplierId,
                supplier_name: supplierName, // Optional, but good for fallback
                total_amount: calculateTotal()
            };

            const response = await ApiService.request({
                method: id ? 'PUT' : 'POST',
                url: id ? `admin/purchases/${id}` : 'admin/purchases',
                data: payload
            });

            if (response.data.status) {
                toast.success(response.data.message);
                navigate('/purchases');
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Something went wrong');
        } finally {
            setIsLoader(false);
        }
    };

    return (
        <div className="d-flex flex-column flex-column-fluid">
            <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                <div id="kt_app_toolbar_container" className="app-container container-fluid d-flex flex-stack">
                    <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                        <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                            {id ? 'Edit Purchase' : 'Create Purchase'}
                        </h1>
                    </div>
                    <div className="d-flex align-items-center gap-2 gap-lg-3">
                        <Link to="/purchases" className="btn btn-sm fw-bold btn-secondary">Back</Link>
                    </div>
                </div>
            </div>
            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div id="kt_app_content_container" className="app-container container-fluid">
                    <div className="card">
                        <div className="card-body">
                            <form onSubmit={handleSubmit}>
                                <div className="row mb-5">
                                    <div className="col-md-4">
                                        <label className="form-label required">Date</label>
                                        <input 
                                            type="date" 
                                            className="form-control form-control-solid"
                                            value={formData.purchase_date}
                                            onChange={(e) => setFormData({...formData, purchase_date: e.target.value})}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label required">Supplier</label>
                                        <div className="mb-2">
                                            <div className="form-check form-check-inline">
                                                <input className="form-check-input" type="radio" name="supplierType" value="existing" checked={supplierType === 'existing'} onChange={() => setSupplierType('existing')} />
                                                <label className="form-check-label">Select Existing</label>
                                            </div>
                                            <div className="form-check form-check-inline">
                                                <input className="form-check-input" type="radio" name="supplierType" value="new" checked={supplierType === 'new'} onChange={() => setSupplierType('new')} />
                                                <label className="form-check-label">Add New</label>
                                            </div>
                                        </div>
                                        {supplierType === 'existing' ? (
                                            <Dropdown 
                                                value={formData.supplier_id} 
                                                options={suppliers} 
                                                onChange={(e) => setFormData({...formData, supplier_id: e.value})} 
                                                optionLabel="name" 
                                                optionValue="id" 
                                                placeholder="Select Supplier" 
                                                filter 
                                                className="w-100" 
                                            />
                                        ) : (
                                            <div className="row g-2">
                                                <div className="col-12">
                                                    <input type="text" className="form-control form-control-solid" placeholder="Name" value={newSupplier.name} onChange={(e) => setNewSupplier({...newSupplier, name: e.target.value})} required />
                                                </div>
                                                <div className="col-6">
                                                    <input type="text" className="form-control form-control-solid" placeholder="Phone" value={newSupplier.phone} onChange={(e) => setNewSupplier({...newSupplier, phone: e.target.value})} />
                                                </div>
                                                <div className="col-6">
                                                    <input type="text" className="form-control form-control-solid" placeholder="Address" value={newSupplier.address} onChange={(e) => setNewSupplier({...newSupplier, address: e.target.value})} />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label required">Status</label>
                                        <select 
                                            className="form-select form-select-solid" 
                                            value={formData.status}
                                            onChange={(e) => setFormData({...formData, status: e.target.value})}
                                        >
                                            <option value="received">Received</option>
                                            <option value="pending">Pending</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="mb-5">
                                    <label className="form-label">Items</label>
                                    <div className="table-responsive">
                                        <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
                                            <thead>
                                                <tr className="fw-bold text-muted">
                                                    <th className="min-w-200px">Product</th>
                                                    <th className="min-w-100px">Unit</th>
                                                    <th className="min-w-100px">Quantity</th>
                                                    <th className="min-w-100px">Unit Price</th>
                                                    <th className="min-w-100px">Subtotal</th>
                                                    <th className="w-50px"></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {formData.items.map((item, index) => (
                                                    <tr key={index}>
                                                        <td>
                                                            <Dropdown 
                                                                value={item.product_id} 
                                                                options={products} 
                                                                onChange={(e) => handleItemChange(index, 'product_id', e.value)} 
                                                                optionLabel="formatted_name" 
                                                                optionValue="id"
                                                                placeholder="Select a Product" 
                                                                filter 
                                                                className="w-100" 
                                                            />
                                                        </td>
                                                        <td>
                                                            {isProductLens(item.product_id) ? (
                                                                <Dropdown 
                                                                    value={item.unit} 
                                                                    options={[{label: 'Piece', value: 'piece'}, {label: 'Pair', value: 'pair'}]} 
                                                                    onChange={(e) => handleItemChange(index, 'unit', e.value)} 
                                                                    className="w-100" 
                                                                />
                                                            ) : (
                                                                <span className="form-control form-control-solid bg-light">Piece</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <input 
                                                                type="number" 
                                                                className="form-control form-control-solid" 
                                                                value={item.quantity}
                                                                onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                                                min="1"
                                                            />
                                                        </td>
                                                        <td>
                                                            <input 
                                                                type="text" 
                                                                className="form-control form-control-solid" 
                                                                value={formatNumberWithCommas(item.unit_price)}
                                                                onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)}
                                                            />
                                                        </td>
                                                        <td>
                                                        <span className="fw-bold">{formatCurrency(parseFloat(item.subtotal || 0))}</span>
                                                    </td>
                                                        <td>
                                                            <button 
                                                                type="button" 
                                                                className="btn btn-icon btn-sm btn-light-danger"
                                                                onClick={() => removeItem(index)}
                                                            >
                                                                <i className="fas fa-trash"></i>
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot>
                                                <tr>
                                                    <td colSpan="6">
                                                        <button type="button" className="btn btn-sm btn-light-primary" onClick={addItem}>
                                                            <i className="fas fa-plus"></i> Add Item
                                                        </button>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td colSpan="4" className="text-end fw-bold fs-4">Total Amount:</td>
                                                    <td className="fw-bold fs-4">{formatCurrency(calculateTotal())}</td>
                                                    <td></td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                </div>

                                <div className="mb-5">
                                    <label className="form-label">Note</label>
                                    <textarea 
                                        className="form-control form-control-solid"
                                        value={formData.note}
                                        onChange={(e) => setFormData({...formData, note: e.target.value})}
                                        rows="3"
                                    ></textarea>
                                </div>

                                <div className="d-flex justify-content-end">
                                    <button type="submit" className="btn btn-base" disabled={isLoader}>
                                        {isLoader ? 'Saving...' : 'Save Purchase'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddUpdate;
